import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'

export interface RdaItem {
  id: number
  rda_id: number
  descrizione: string
  created_at: string
}

export interface Rda {
  id: number
  intervention_id: number
  work_order_id: number
  note?: string | null
  items: RdaItem[]
  created_at: string
}

export const useRdasForWorkOrder = (workOrderId?: number) =>
  useQuery({
    queryKey: ['rdas-work-order', workOrderId],
    queryFn: async () => {
      const res = await axiosInstance.get<Rda[]>(`/rdas/work-order/${workOrderId}`)
      return res.data
    },
    enabled: !!workOrderId,
  })

export const useRdaIntervento = (interventionId?: number) =>
  useQuery({
    queryKey: ['rda-intervento', interventionId],
    queryFn: async () => {
      try {
        const res = await axiosInstance.get<Rda>(`/rdas/intervention/${interventionId}`)
        return res.data
      } catch (err: any) {
        if (err?.response?.status === 404) return null
        throw err
      }
    },
    enabled: !!interventionId,
  })

export const useCreateRda = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { intervention_id: number; work_order_id: number }) => {
      const res = await axiosInstance.post<Rda>('/rdas/', data)
      return res.data
    },
    onSuccess: (rda) => {
      qc.setQueryData(['rda-intervento', rda.intervention_id], rda)
      qc.invalidateQueries({ queryKey: ['rdas-work-order', rda.work_order_id] })
    },
    onError: () => message.error('Errore nella creazione della RDA'),
  })
}

export const useAddRdaItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rdaId, descrizione }: { rdaId: number; descrizione: string }) => {
      const res = await axiosInstance.post<RdaItem>(`/rdas/${rdaId}/items`, { descrizione })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rda-intervento'] })
      qc.invalidateQueries({ queryKey: ['rdas-work-order'] })
    },
    onError: () => message.error("Errore nell'aggiunta dell'articolo"),
  })
}

export const useUpdateRda = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rdaId, note }: { rdaId: number; note: string }) => {
      const res = await axiosInstance.patch<Rda>(`/rdas/${rdaId}`, { note })
      return res.data
    },
    onSuccess: (rda) => {
      qc.setQueryData(['rda-intervento', rda.intervention_id], rda)
      qc.invalidateQueries({ queryKey: ['rdas-work-order', rda.work_order_id] })
    },
    onError: () => message.error('Errore nel salvataggio della nota'),
  })
}

export const useDeleteRdaItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rdaId, itemId }: { rdaId: number; itemId: number }) => {
      await axiosInstance.delete(`/rdas/${rdaId}/items/${itemId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rda-intervento'] })
      qc.invalidateQueries({ queryKey: ['rdas-work-order'] })
    },
    onError: () => message.error("Errore nell'eliminazione dell'articolo"),
  })
}
